import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import VkSubscribeWidget from '../../../components/vk/VkSubscribeWidget';
import { subscribeToPetReport } from '../../../services/cable';
import { CheckCircleFilledIcon, LocationIcon, CalendarIcon } from '../../pets/pages/ReportDetailIcons';
import { BASE_URL } from '../../../services/api';

function normalizeAsset(url) {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
    try {
        const base = BASE_URL || window.location.origin;
        return new URL(url, base).toString();
    } catch {
        return url;
    }
}

export default function ReportSuccessStep({
    reportId,
    initialReport = null,
    reportType = 'lost',
    petName = '',
    occurredDate = '',
    photos = [],
    locationAddress = '',
    locationMunicipality = '',
    onClose = () => { },
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [liveReport, setLiveReport] = useState(initialReport);

    // Subscribe to ActionCable for real-time background processing updates
    useEffect(() => {
        if (!reportId) return;

        const subscription = subscribeToPetReport(reportId, {
            onConnected() {
                console.log(`[ActionCable] Subscribed to PetReportChannel for report #${reportId}`);
            },
            onReceived(data) {
                console.log(`[ActionCable] Received broadcast for report #${reportId}:`, data);
                if (data?.report) {
                    setLiveReport(data.report);
                }
            }
        });

        return () => {
            if (subscription && subscription.unsubscribe) {
                subscription.unsubscribe();
            }
        };
    }, [reportId]);

    // Resolve pet name (if applicable)
    const resolvedPetName = (liveReport?.pet?.name || liveReport?.pet_name || petName || '').trim();
    const hasPetName = Boolean(resolvedPetName);
    const isLost = liveReport?.report_type === 'lost' || liveReport?.report_type === 0 || reportType === 'lost';

    // Resolve photo preview
    const photoUrl = useMemo(() => {
        if (photos && photos.length > 0) {
            const first = photos[0];
            return first?.previewUrl || first?.url || (typeof first === 'string' ? first : null);
        }
        if (liveReport?.photos && liveReport.photos.length > 0) {
            return liveReport.photos[0]?.url || liveReport.photos[0];
        }
        if (liveReport?.photo_urls && liveReport.photo_urls.length > 0) {
            return liveReport.photo_urls[0];
        }
        return null;
    }, [photos, liveReport]);

    // Resolve event date in dd/mm/yyyy format
    const rawDate = liveReport?.event_occurred_at || occurredDate;
    const formattedDate = useMemo(() => {
        if (!rawDate) return '';
        try {
            if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
                const [y, m, d] = rawDate.split('T')[0].split('-');
                return `${d}/${m}/${y}`;
            }
            const d = new Date(rawDate);
            if (Number.isNaN(d.getTime())) return '';
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        } catch {
            return '';
        }
    }, [rawDate]);

    // Resolve location
    const displayLocation = useMemo(() => {
        return liveReport?.location_description || locationAddress || liveReport?.municipality || locationMunicipality || '';
    }, [liveReport, locationAddress, locationMunicipality]);

    // Compute public report URL
    const publicUrlPath = useMemo(() => {
        const rep = liveReport;
        if (rep?.slug && rep?.country && rep?.municipality_slug) {
            return `/reports/${rep.country.toLowerCase()}/${rep.municipality_slug}/${reportType}/${rep.slug}`;
        }
        if (rep?.post_slug) {
            return `/posts/${rep.post_slug}`;
        }
        if (rep?.post?.slug) {
            return `/posts/${rep.post.slug}`;
        }
        if (reportId) {
            return `/posts/${reportId}`;
        }
        return '/dashboard';
    }, [liveReport, reportType, reportId]);

    const handleViewReport = async () => {
        try {
            if (typeof onClose === 'function') {
                await onClose();
            }
        } catch (e) {
            console.error('Error during onClose in ReportSuccessStep:', e);
        } finally {
            if (publicUrlPath === '/dashboard') {
                if (window.location.pathname.replace(/\/$/, '') === '/dashboard') {
                    window.location.reload();
                } else {
                    window.location.href = '/dashboard';
                }
            } else {
                navigate(publicUrlPath);
            }
        }
    };

    const handleGoDashboard = async () => {
        try {
            if (typeof onClose === 'function') {
                await onClose();
            }
        } catch (e) {
            console.error('Error during onClose in ReportSuccessStep:', e);
        } finally {
            if (window.location.pathname.replace(/\/$/, '') === '/dashboard') {
                window.location.reload();
            } else {
                window.location.href = '/dashboard';
            }
        }
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-card-dark relative animate-fadeIn">
            {/* Top decorative gradient */}
            <div className={`absolute top-0 left-0 right-0 h-36 ${isLost ? 'bg-gradient-to-b from-orange-50/60 dark:from-orange-950/20' : 'bg-gradient-to-b from-emerald-50/60 dark:from-emerald-950/20'} to-transparent pointer-events-none`} />

            <div className="flex-1 flex flex-col p-4 sm:p-6 relative z-10 overflow-y-auto">
                {/* Header Icon & Titles */}
                <div className="text-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary/20 text-primary flex items-center justify-center mx-auto mb-2 shadow-sm">
                        <CheckCircleFilledIcon className="w-7 h-7 text-primary" />
                    </div>

                    <h2 className="text-lg sm:text-xl font-semibold text-foreground-light dark:text-foreground-dark leading-tight">
                        {isLost
                            ? t('pets.success_friendly_title', { defaultValue: 'Búsqueda Iniciada' })
                            : t('pets.success_report_found_title', { defaultValue: 'Reporte Publicado' })}
                    </h2>

                    <p className="text-xs sm:text-sm text-subtle-light dark:text-subtle-dark mt-1 max-w-sm mx-auto leading-relaxed">
                        {isLost
                            ? t('pets.success_friendly_desc', { defaultValue: 'Hemos activado el protocolo de búsqueda.' })
                            : t('pets.success_report_found_desc', { defaultValue: 'Tu reporte está activo y visible para toda la comunidad cercana.' })}
                    </p>
                </div>

                {/* Pet Report Summary (No card) */}
                <div className="flex items-start gap-3.5 sm:gap-4 mt-1">
                    {photoUrl ? (
                        <img
                            src={normalizeAsset(photoUrl)}
                            alt={resolvedPetName || (isLost ? t('common.lost') : t('common.found'))}
                            className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover shrink-0 border border-border-light dark:border-border-dark bg-neutral-100 dark:bg-neutral-800"
                        />
                    ) : (
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-neutral-100 dark:bg-neutral-800 shrink-0 flex items-center justify-center text-subtle-light dark:text-subtle-dark text-2xl font-semibold border border-border-light dark:border-border-dark">
                            🐾
                        </div>
                    )}

                    <div className="min-w-0 flex-1 flex flex-col pt-0.5">
                        {/* Name (if applicable) + Badge */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {hasPetName && (
                                <h3 className="text-sm font-medium text-foreground-light dark:text-foreground-dark leading-tight truncate">
                                    {resolvedPetName}
                                </h3>
                            )}
                            <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium text-white leading-none select-none ${
                                    isLost ? 'bg-danger-light' : 'bg-primary'
                                }`}
                            >
                                {isLost ? t('common.lost') : t('common.found')}
                            </span>
                        </div>

                        {/* Date below */}
                        {formattedDate && (
                            <div className="text-xs text-subtle-light dark:text-subtle-dark flex items-center gap-1.5 mt-1.5">
                                <CalendarIcon className="w-3.5 h-3.5 shrink-0 text-subtle-light dark:text-subtle-dark" />
                                <span>{formattedDate}</span>
                            </div>
                        )}

                        {/* Location below (wraps to next line if needed) */}
                        {displayLocation && (
                            <div className="text-xs text-subtle-light dark:text-subtle-dark flex items-start gap-1.5 mt-2 leading-snug">
                                <LocationIcon className="w-3.5 h-3.5 shrink-0 text-subtle-light dark:text-subtle-dark mt-0.5" />
                                <span className="break-words">{displayLocation}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* VK Community Widget */}
                <div className="mt-3 sm:mt-4 flex flex-col items-center w-full">
                    <div className="w-full rounded-xl border border-gray-100 dark:border-border-dark overflow-hidden bg-gray-50 dark:bg-card-dark">
                        <VkSubscribeWidget
                            widgetType="group"
                            mode={3}
                            width="auto"
                            color1="F9FAFB"
                            color2="1C1C3B"
                            color3="0077FF"
                            noCover={true}
                        />
                    </div>

                    <p className="text-xs font-medium text-foreground-light dark:text-foreground-dark text-center mt-2.5 px-2">
                        {t('pets.success_vk_subscribe_hint', { defaultValue: 'Suscríbete a nuestra comunidad para recibir notificaciones' })}
                    </p>
                </div>
            </div>

            {/* Fixed Footer Buttons (outside scroll) */}
            <div className="bg-white dark:bg-card-dark px-4 sm:px-6 py-3 border-t border-border-light dark:border-border-dark shrink-0 z-20 pb-safe">
                <div className="grid grid-cols-2 gap-3 w-full">
                    <Button
                        type="button"
                        onClick={handleGoDashboard}
                        variant="neutral"
                        size="md"
                        className="w-full font-medium sm:!py-2 sm:!text-sm"
                    >
                        {t('pets.success_go_dashboard_btn', { defaultValue: 'Dashboard' })}
                    </Button>

                    <Button
                        type="button"
                        onClick={handleViewReport}
                        variant="primary"
                        size="md"
                        className="w-full font-medium sm:!py-2 sm:!text-sm"
                    >
                        {t('pets.success_view_report_btn', { defaultValue: 'Ver reporte' })}
                    </Button>
                </div>
            </div>
        </div>
    );
}
